/**
 * The Power and Energy preview, at `/power`: a solar roof
 * (docs/foundations.md §8).
 *
 * The one real model here is the sun. `planeYield` walks the sun across one
 * day in the middle of every month, half an hour at a time, and adds up the
 * clear-sky light on a panel of a given tilt and direction. It is a clear-sky
 * model (no clouds), so it is used only for *ratios* — how much better one
 * angle is than another, how the months compare — and never as a forecast.
 * The yearly figures per kilowatt of panels are typical published ones for
 * each city, after clouds and losses, and are named as such.
 *
 * Everything is deterministic: the same settings always give the same
 * verdict, here, in the tests, and anywhere else that runs this file.
 */

export const POWER_STEP_IDS = ['tilt', 'array', 'battery', 'cable', 'power-ai'] as const;
export type PowerStepId = (typeof POWER_STEP_IDS)[number];

const RAD = Math.PI / 180;

/* ── The sun ───────────────────────────────────────────────────────────── */

export const CITY_IDS = ['cairo', 'berlin', 'tokyo', 'nairobi', 'sydney'] as const;
export type CityId = (typeof CITY_IDS)[number];

export interface City {
  /** Degrees, north positive. */
  lat: number;
  /** Typical yearly output of 1 kW of panels, well pointed, after clouds and losses, kWh. */
  kwhPerKw: number;
}

export const CITIES: Record<CityId, City> = {
  cairo: { lat: 30.0, kwhPerKw: 1750 },
  berlin: { lat: 52.5, kwhPerKw: 950 },
  tokyo: { lat: 35.7, kwhPerKw: 1200 },
  nairobi: { lat: -1.3, kwhPerKw: 1650 },
  sydney: { lat: -33.9, kwhPerKw: 1450 },
};

/** Which way a panel faces, as degrees from south, west positive (the solar convention). */
export const FACING = { N: 180, NE: -135, E: -90, SE: -45, S: 0, SW: 45, W: 90, NW: 135 } as const;
export type Facing = keyof typeof FACING;
export const FACING_IDS = Object.keys(FACING) as Facing[];

/** Middle day of each month. */
const MID_DAYS = [17, 47, 75, 105, 135, 162, 198, 228, 258, 288, 318, 344];
const DAYS_IN = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * Clear-sky light on a tilted plane, kWh/m², month by month.
 *
 * Duffie and Beckman's angle of incidence, Meinel's clear-sky beam
 * (1353 × 0.7^(AM^0.678)), a tenth of that again as sky light spread evenly,
 * and a fifth of the ground's light reflected up. Half-hour steps.
 */
export function monthlyPlane(lat: number, tilt: number, facing: number): number[] {
  const phi = lat * RAD;
  const beta = tilt * RAD;
  const gamma = facing * RAD;
  return MID_DAYS.map((n, m) => {
    const delta = 23.45 * Math.sin((360 * (284 + n) * RAD) / 365) * RAD;
    let wh = 0;
    for (let t = 0.25; t < 24; t += 0.5) {
      const omega = 15 * (t - 12) * RAD;
      const sinAlt = Math.sin(phi) * Math.sin(delta) + Math.cos(phi) * Math.cos(delta) * Math.cos(omega);
      if (sinAlt <= 0.02) continue;
      const am = 1 / sinAlt;
      const beam = 1353 * 0.7 ** (am ** 0.678);
      const diffuseH = 0.1 * beam;
      const globalH = beam * sinAlt + diffuseH;
      const cosTheta =
        Math.sin(delta) * Math.sin(phi) * Math.cos(beta) -
        Math.sin(delta) * Math.cos(phi) * Math.sin(beta) * Math.cos(gamma) +
        Math.cos(delta) * Math.cos(phi) * Math.cos(beta) * Math.cos(omega) +
        Math.cos(delta) * Math.sin(phi) * Math.sin(beta) * Math.cos(gamma) * Math.cos(omega) +
        Math.cos(delta) * Math.sin(beta) * Math.sin(gamma) * Math.sin(omega);
      const onPlane =
        beam * Math.max(0, cosTheta) + diffuseH * ((1 + Math.cos(beta)) / 2) + 0.2 * globalH * ((1 - Math.cos(beta)) / 2);
      wh += onPlane * 0.5;
    }
    return (wh * (DAYS_IN[m] ?? 30)) / 1000;
  });
}

export function planeYield(lat: number, tilt: number, facing: number): number {
  return monthlyPlane(lat, tilt, facing).reduce((a, b) => a + b, 0);
}

export const TILT_STOPS = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90] as const;

const bestCache = new Map<number, number>();

/** The best yearly light any tilt and direction on the stops can catch at this latitude. */
export function bestYield(lat: number): number {
  const hit = bestCache.get(lat);
  if (hit !== undefined) return hit;
  let best = 0;
  for (const tilt of TILT_STOPS) for (const f of FACING_IDS) best = Math.max(best, planeYield(lat, tilt, FACING[f]));
  bestCache.set(lat, best);
  return best;
}

/** How well a setup is pointed: its yearly light over the best possible, 0 to 1. */
export function pointing(city: CityId, tilt: number, facing: Facing): number {
  const lat = CITIES[city].lat;
  return planeYield(lat, tilt, FACING[facing]) / bestYield(lat);
}

/* ── 1. Point the panel ────────────────────────────────────────────────── */

/** Within this share of the best is "well pointed". */
export const POINTING_TARGET = 0.97;

export function checkTilt(city: CityId, tilt: number, facing: Facing): { ok: boolean; share: number } {
  const share = pointing(city, tilt, facing);
  return { ok: share >= POINTING_TARGET, share };
}

/* ── 2. Size the array ─────────────────────────────────────────────────── */

/** One panel, kW. */
export const PANEL_KW = 0.4;
/** Panels the roof has room for. */
export const ROOF_PANELS = 14;
/** The Cairo home's use, month by month, kWh: air conditioning makes summer the peak. */
export const CAIRO_USE = [400, 380, 410, 440, 530, 630, 700, 710, 600, 470, 390, 390];
/** Cover at least this much of the year's use, and do not build more than this. */
export const COVER_MIN = 0.8;
export const COVER_MAX = 1.2;

/** Month-by-month output of `panels` panels in a city, pointed at the latitude, due to the equator. */
export function monthlyOutput(city: CityId, panels: number, tilt?: number): number[] {
  const c = CITIES[city];
  const facing = c.lat >= 0 ? FACING.S : FACING.N;
  const at = tilt ?? Math.round(Math.abs(c.lat) / 10) * 10;
  const shape = monthlyPlane(c.lat, at, facing);
  const share = pointing(city, at, c.lat >= 0 ? 'S' : 'N');
  const total = shape.reduce((a, b) => a + b, 0);
  const year = panels * PANEL_KW * c.kwhPerKw * share;
  return shape.map((m) => (m / total) * year);
}

export interface ArrayResult {
  ok: boolean;
  madeKwh: number;
  usedKwh: number;
  cover: number;
  broken: 'short' | 'over' | 'roof' | null;
}

export function sizeArray(city: CityId, panels: number, use: readonly number[], tilt?: number): ArrayResult {
  const madeKwh = monthlyOutput(city, panels, tilt).reduce((a, b) => a + b, 0);
  const usedKwh = use.reduce((a, b) => a + b, 0);
  const cover = madeKwh / usedKwh;
  const broken = panels > ROOF_PANELS ? 'roof' : cover < COVER_MIN ? 'short' : cover > COVER_MAX ? 'over' : null;
  return { ok: broken === null, madeKwh, usedKwh, cover, broken };
}

/* ── 3. A battery for the night ────────────────────────────────────────── */

/** What the home uses between sunset and sunrise, kWh. */
export const NIGHT_KWH = 6;
/** The share of a battery's capacity that may be used without wearing it out. */
export const USABLE = 0.9;
/** Price per kWh of battery, US dollars, and the owner's budget. */
export const BATTERY_USD_PER_KWH = 450;
export const BATTERY_BUDGET = 4000;
export const BATTERY_STOPS = [2.5, 5, 7.5, 10, 12.5] as const;

export interface BatteryResult {
  ok: boolean;
  usableKwh: number;
  costUsd: number;
  /** Hours after sunset (18:00) the battery lasts, capped at the 12-hour night. */
  lastsHours: number;
  broken: 'short' | 'budget' | null;
}

/**
 * The night's use, hour by hour from 18:00 to 06:00: busy evening, quiet
 * small hours. Sums to NIGHT_KWH.
 */
export const NIGHT_HOURS = [0.9, 0.9, 0.8, 0.7, 0.5, 0.35, 0.3, 0.3, 0.3, 0.3, 0.4, 0.25];

export function battery(capacityKwh: number): BatteryResult {
  const usableKwh = capacityKwh * USABLE;
  const costUsd = capacityKwh * BATTERY_USD_PER_KWH;
  let left = usableKwh;
  let lastsHours = 0;
  for (const h of NIGHT_HOURS) {
    if (left + 1e-9 < h) {
      lastsHours += Math.max(0, left / h);
      left = 0;
      break;
    }
    left -= h;
    lastsHours += 1;
  }
  const broken = usableKwh + 1e-9 < NIGHT_KWH ? 'short' : costUsd > BATTERY_BUDGET ? 'budget' : null;
  return { ok: broken === null, usableKwh, costUsd, lastsHours, broken };
}

/* ── 4. The cable to the house ─────────────────────────────────────────── */

/** Inverter to the main board, metres; the current at full sun, amps; the voltage. */
export const CABLE_M = 25;
export const CABLE_A = 25;
export const MAINS_V = 230;
/** Copper, Ω·mm²/m. */
const COPPER = 0.0172;
/** Allowed voltage drop, a share of the voltage. */
export const DROP_LIMIT = 0.03;
export const SIZE_STOPS = [1.5, 2.5, 4, 6, 10] as const;
/** Safe current for each size in a wall, amps (simplified). */
export const SIZE_AMPS: Record<(typeof SIZE_STOPS)[number], number> = { 1.5: 16, 2.5: 24, 4: 32, 6: 41, 10: 57 };

export interface CableResult {
  ok: boolean;
  dropV: number;
  dropShare: number;
  ratingA: number;
  broken: 'hot' | 'drop' | null;
}

/** Out and back: drop = 2 × length × current × ρ / area. */
export function cable(sizeMm2: (typeof SIZE_STOPS)[number]): CableResult {
  const dropV = (2 * CABLE_M * CABLE_A * COPPER) / sizeMm2;
  const dropShare = dropV / MAINS_V;
  const ratingA = SIZE_AMPS[sizeMm2];
  const broken = ratingA < CABLE_A ? 'hot' : dropShare > DROP_LIMIT ? 'drop' : null;
  return { ok: broken === null, dropV, dropShare, ratingA, broken };
}

/* ── 5. Iris moves the plan to Berlin ──────────────────────────────────── */

/** The Berlin flat's use: no air conditioning, more light and heat in winter. */
export const BERLIN_USE = [420, 370, 350, 300, 280, 260, 260, 270, 290, 330, 380, 430];
/** Iris's plan, copied from Cairo. */
export const IRIS_PANELS = 8;
export const IRIS_TILT = 30;
